import React from 'react';
import { Formik } from 'formik';
import { Form, Row, Col, Container } from 'react-bootstrap';

import './MemberServiceAccount.scss';
import GenericButton from 'src/Components/GenericButton/GenericButton';
import Header from 'src/Admin/components/common/Header/Header';
import { LoginSVG } from 'src/Member/img/GlobalSVG';
import useAccountUnlockResetPassword from './hooks/useAccountUnlockResetPassword';

const AccountUnlockResetPassword = ({ onSubmit }) => {
  const {
    schema,
    showPassword,
    showConfirmPassword,
    setShowPassword,
    setShowConfirmPassword,
    unlockAccount,
    headingRef,
  } = useAccountUnlockResetPassword();

  return (
    <Container className="reset-password-page">
      <div>
        <div
          role="heading"
          aria-level={1}
          className="sr-only"
          tabIndex={-1}
          ref={headingRef}
        >
          Reset Password
        </div>
        <Header
          linkTo={`/member/password-recovery-new`}
          header={'Reset Password'}
          className="account-recovery-header"
        />
      </div>
      <Row>
        <Col>
          <p className="reset-password-description">
            Let&apos;s secure your account.
          </p>
        </Col>
      </Row>

      <Row className="account-reset-password-form">
        <Col md={8} lg={6}>
          <Formik
            validationSchema={schema}
            initialValues={{
              newPassword: '',
              confirmPassword: '',
            }}
            onSubmit={(values) => {
              unlockAccount(values);
              //onSubmit?.(values);
            }}
          >
            {({
              handleSubmit,
              handleChange,
              handleBlur,
              values,
              errors,
              touched,
            }) => {
              const passwordRules = {
                length: values.newPassword.length >= 8,
                lowercase: /[a-z]/.test(values.newPassword),
                uppercase: /[A-Z]/.test(values.newPassword),
                number: /[0-9]/.test(values.newPassword),
                special: /[~`!@#$%^&*()_\-+={[}\]|\\:;"'<,>.?/]/.test(
                  values.newPassword,
                ),
              };

              const strength =
                Object.values(passwordRules).filter(Boolean).length;

              return (
                <Form onSubmit={handleSubmit} className="reset-password-form">
                  <Form.Group className="mb-4" controlId="NewPassword">
                    <Form.Label>
                      New Password
                      <span className="required-mark">*</span>
                    </Form.Label>
                    <div className="password-input-wrapper">
                      <Form.Control
                        type={showPassword ? 'text' : 'password'}
                        name="newPassword"
                        value={values.newPassword}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.newPassword && !!errors.newPassword}
                        placeholder="Maintain password rules"
                      />
                      <span
                        role={'button'}
                        onClick={(event) => {
                          setShowPassword(!showPassword);
                        }}
                        onKeyPress={(event) => {
                          setShowPassword(!showPassword);
                        }}
                        tabIndex={0}
                        className="password-eye-icon"
                        aria-label={
                          showConfirmPassword
                            ? 'show Password'
                            : 'hide password'
                        }
                      >
                        {showPassword ? (
                          <LoginSVG.DisplayPasswordSVG />
                        ) : (
                          <LoginSVG.HidePasswordSVG />
                        )}
                      </span>
                    </div>
                    <Form.Control.Feedback type="invalid">
                      {errors.newPassword}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <div className="password-rules-container">
                    <div className="password-rules">
                      <span className="password-rules-title">
                        Password Rules:
                      </span>
                      <div className="password-rules-point">
                        <div
                          className={`rule ${
                            passwordRules.length ? 'rule-valid' : ''
                          }`}
                        >
                          <span className="rule-dot" />
                          At least 8 characters Long
                        </div>

                        <div
                          className={`rule ${
                            passwordRules.lowercase ? 'rule-valid' : ''
                          }`}
                        >
                          <span className="rule-dot" />
                          At least 1 lower case characters (a-z)
                        </div>

                        <div
                          className={`rule ${
                            passwordRules.uppercase ? 'rule-valid' : ''
                          }`}
                        >
                          <span className="rule-dot" />
                          At least 1 Upper case characters (A-Z)
                        </div>

                        <div
                          className={`rule ${
                            passwordRules.number ? 'rule-valid' : ''
                          }`}
                        >
                          <span className="rule-dot" />
                          At least 1 number (0-9)
                        </div>

                        <div
                          className={`rule ${
                            passwordRules.special ? 'rule-valid' : ''
                          }`}
                        >
                          <span className="rule-dot" />
                          At least 1 symbol (Example: ~!@#$)
                        </div>
                      </div>
                    </div>

                    <div className="password-strength">
                      <span className="password-rules-title">
                        Password Strength
                      </span>

                      <div className="strength-bars">
                        {[1, 2, 3, 4].map((bar) => (
                          <span
                            key={bar}
                            className={`strength-bar ${
                              strength >= bar ? 'active' : ''
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <Form.Group className="mb-4" controlId="ConfirmPassword">
                    <Form.Label>
                      Confirm Password
                      <span className="required-mark">*</span>
                    </Form.Label>
                    <div className="password-input-wrapper confirm-password-input">
                      <Form.Control
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={values.confirmPassword}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={
                          touched.confirmPassword && !!errors.confirmPassword
                        }
                      />

                      <span
                        role={'button'}
                        onClick={(event) => {
                          setShowConfirmPassword(!showConfirmPassword);
                        }}
                        onKeyPress={(event) => {
                          setShowConfirmPassword(!showConfirmPassword);
                        }}
                        tabIndex={0}
                        className="password-eye-icon"
                        aria-label={
                          showConfirmPassword
                            ? 'show Password'
                            : 'hide password'
                        }
                      >
                        {showConfirmPassword ? (
                          <LoginSVG.DisplayPasswordSVG />
                        ) : (
                          <LoginSVG.HidePasswordSVG />
                        )}
                      </span>
                    </div>

                    <Form.Control.Feedback type="invalid">
                      {errors.confirmPassword}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Row className="reset-password-actions">
                    <Col md={12} className="text-right">
                      <GenericButton
                        type="submit"
                        variant="primary"
                        className="reset-password-submit"
                      >
                        Continue
                      </GenericButton>
                    </Col>
                  </Row>
                </Form>
              );
            }}
          </Formik>
        </Col>
      </Row>
    </Container>
  );
};

export default AccountUnlockResetPassword;
